<!--
  Everything in here can be text the target chose, except two things: the host
  offered as a one-click correction, and the advice line under a refusal the
  form can do something about. That host is parsed from what the user typed
  into the form, never from `UserError.message`, which the schema says is prose
  for a human and may change without notice; the advice is this build's own
  copy for the same reason. Matching happens on `code` only, and a code this
  build has never seen still renders — the schema's own guidance for
  `UserErrorCode` is to treat the unknown case as a generic rejection rather
  than hide it.
-->
<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import RetryCountdown from './RetryCountdown.vue'

interface NoticeError {
  readonly code: string
  readonly message: string
  readonly path: readonly string[]
}

const props = defineProps<{
  errors: readonly NoticeError[]
  retryAfterSeconds?: number | null
  typedTarget: string
}>()

const emit = defineEmits<{
  (e: 'correct', target: string): void
}>()

const root = ref<HTMLElement | null>(null)

defineExpose({
  async focus() {
    await nextTick()
    root.value?.focus()
  },
})

function tryHttpHost(value: string): string | null {
  try {
    const url = new URL(value)
    if (url.protocol === 'http:' || url.protocol === 'https:') return url.host
  } catch {
    // Not a URL at all. The caller falls back to the https:// prefix.
  }
  return null
}

/**
 * `url.host` rather than `url.hostname`, so a port the user typed survives
 * the correction instead of being silently dropped.
 */
function hostFromTyped(typed: string): string | null {
  return tryHttpHost(typed) ?? tryHttpHost(`https://${typed}`)
}

const correctionHost = computed(() => hostFromTyped(props.typedTarget))

// The button that fires this only renders when `correctionHost` is set (see
// the `v-if` below), so the value is never null here.
function onCorrectClick() {
  emit('correct', correctionHost.value as string)
}

function showRetry(code: string): boolean {
  return (
    (code === 'RATE_LIMITED' || code === 'CAPACITY_EXCEEDED') &&
    typeof props.retryAfterSeconds === 'number'
  )
}

/**
 * What the visitor can do about a refusal, in this build's own words.
 *
 * The server's `message` says what went wrong and is rendered above this. These
 * lines say what to do next, and they are written here rather than read off the
 * payload because the schema calls `message` prose that may change without
 * notice, and because what to do next depends on the controls this form
 * actually has.
 *
 * The two sweep refusals are the ones worth the extra line, and they pull in
 * opposite directions. PORT_SCAN_AUTHORIZATION_NOT_CONFIRMED is a box the
 * visitor can tick and send again. PORT_SCANNING_DISABLED is the operator's
 * configuration, which no amount of asking will change, so the line has to send
 * them somewhere other than the submit button. The schema deliberately gives
 * one code for both "no sweeps at all" and "not that profile", so this does not
 * suggest trying a different profile either.
 */
const ADVICE: Readonly<Record<string, string>> = {
  PORT_SCAN_AUTHORIZATION_NOT_CONFIRMED:
    'Tick the port sweep authorization box and submit again, or set the sweep back to "No port sweep" to scan without one.',
  PORT_SCANNING_DISABLED:
    'Which ports this server will sweep, if any, is the operator\'s decision, so asking again will not help. Set the sweep back to "No port sweep" to get the rest of the report.',
}

function adviceFor(code: string): string | undefined {
  return ADVICE[code]
}
</script>

<template>
  <div
    ref="root"
    role="alert"
    tabindex="-1"
    data-tone="high"
    class="card tone-border flex flex-col gap-3 p-4 outline-none"
  >
    <div
      v-for="(error, index) in props.errors"
      :key="index"
      class="flex flex-col gap-2"
    >
      <p class="text-ink">
        {{ error.message }}
      </p>
      <p
        v-if="adviceFor(error.code)"
        class="text-sm text-ink-muted"
      >
        {{ adviceFor(error.code) }}
      </p>
      <button
        v-if="error.code === 'TARGET_LOOKS_LIKE_URL' && correctionHost"
        type="button"
        class="tap w-fit rounded-md border border-line-strong px-3 font-medium text-ink transition-colors duration-100 hover:bg-sunken active:translate-y-px"
        @click="onCorrectClick"
      >
        Scan {{ correctionHost }} instead
      </button>
      <RetryCountdown
        v-else-if="showRetry(error.code)"
        :seconds="(props.retryAfterSeconds as number)"
      />
    </div>
  </div>
</template>

<style scoped>
/* No :style binding, per the house rule: data-tone sets the token, this class
   just applies it. */
.tone-border {
  border-left: 3px solid var(--tone-accent);
}
</style>
